<template>
  <div>
    <mds-input
      v-bind="$attrs"
      :hidden-label="hiddenLabel"
      :required="required"
      :readonly="readonly"
      :right-aligned="rightAligned"
      :value="formatNum(savedValue)"
      :label="label"
      :size="size"
      :maxlength="maxlength"
      :placeholder="placeholder"
      :error="error"
      :error-text="errorText"
      autocomplete="off"
      @blur="processConfirmedInput"
      @focus="e => $emit('focus', e)"
      @input="filterInput"
      @keydown.enter="processConfirmedInput"
    ></mds-input>
  </div>
</template>

<script>
import MdsInput from '@mds/input';

export default {
  name: 'hypo-number-input',
  components: { MdsInput },
  inheritAttrs: false,
  props: {
    rightAligned: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    savedValue: {
      type: Number,
      default: null,
    },
    isPercent: {
      type: Boolean,
      default: false,
    },
    range: {
      type: Object,
      default: () => {},
    },
    size: {
      type: String,
      default: 'medium',
    },
    maxlength: {
      type: Number,
      default: 15,
    },
    hiddenLabel: {
      type: Boolean,
      default: true,
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    fractionDigits: {
      type: Number,
      default: 2,
    },
    maxFractionDigits: {
      type: Number,
    },
    placeholder: {
      type: String,
      default: '',
    },
    allowNegative: {
      type: Boolean,
      default: true,
    },
    allowLeaveBlank: {
      type: Boolean,
      default: true,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorText: {
      type: Array,
      default: () => [''],
    },
  },
  computed: {
    validMaxFractionDigits() {
      const max = this.maxFractionDigits;
      if (max && max > this.fractionDigits) {
        return max;
      }
      return this.fractionDigits;
    },
  },
  methods: {
    emitEvent(payload) {
      this.$emit('update-saved-value', payload);
    },
    async processConfirmedInput(e) {
      e.preventDefault();
      // Allow user leave blank
      if (e.target.value === '' && this.allowLeaveBlank) {
        this.emitEvent(undefined);
        return;
      }
      const input = this.parseNumber(e.target.value, this.validMaxFractionDigits);
      // If the input is invalid or equal previous value, revert to previously saved value
      if (this.isCurrentInputInvalid(input) || input === this.savedValue) {
        e.target.value = this.formatNum(this.savedValue);
        if (this.isCurrentInputInvalid(input)) {
          this.$emit('input-invalid');
        }
        return;
      }
      e.target.value = this.formatNum(input);
      this.emitEvent(input);
    },
    isCurrentInputInvalid(num) {
      if (Number.isNaN(num)) {
        return true;
      }
      if (
        this.range &&
        typeof this.range.min !== 'undefined' &&
        typeof this.range.max !== 'undefined' &&
        this.isNumber(this.range.min) &&
        this.isNumber(this.range.max)
      ) {
        const { min, max } = this.range;
        if (Math.abs(num - min) < 0.00001 || Math.abs(num - max) < 0.00001) {
          return false;
        }
        return num < min || num > max;
      }
      const isNegative = num < 0;
      if (this.isPercent) {
        return isNegative || num > 100;
      }
      return !this.allowNegative && isNegative;
    },
    filterInput(value, e) {
      // Only accept "0-9" and "."
      e.target.value = value.replace(/[^0-9.+-]/g, '');
    },
    formatNum(num) {
      // 10 => 10.00, 1000 => 1,000.00
      // leave undefined to use the visitor's browser
      // locale or a string like 'en-US' to override it.
      if (!this.isNumber(num)) {
        return '';
      }
      return num.toLocaleString(undefined, {
        minimumFractionDigits: this.fractionDigits,
        maximumFractionDigits: this.validMaxFractionDigits,
      });
    },
    isNumber(str) {
      const reg = /^[+-]?[\d,]+(.[\d]+)?$/; // match the numbers such as '123', '+123', '-123', '123.00', '123,456.00'
      return reg.test(str);
    },
    roundNumber(number, precision = 2) {
      // use number exponential notation to avoid 1.005 round to 1
      const precisionNumber = Number(`${number}e${precision}`);
      return Math.round(precisionNumber) / 10 ** precision;
    },
    parseNumber(str, decimal = 2) {
      // keep 2 decimal places
      if (this.isNumber(str)) {
        const num = parseFloat(str.replace(/,/g, ''));
        return this.roundNumber(num, decimal);
      }
      return NaN;
    },
  },
};
</script>

<style lang="scss"></style>
